import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  customerApi, adminApi, businessApi,
  getCustomerTokenKey, getCustomerProfileKey,
  ADMIN_TOKEN_KEY, OWNER_TOKEN_KEY,
} from '../api/apiClient';

const AuthContext = createContext(null);

const ADMIN_PROFILE = 'nooshin_admin_profile';
const OWNER_PROFILE = 'nooshin_owner_profile';

const load = (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };

export function AuthProvider({ children }) {
  // Customer state is initialized from the per-slug key so each subdomain
  // gets its own independent session.
  const [customer,      setCustomer]      = useState(() => load(getCustomerProfileKey()));
  const [adminUser,     setAdminUser]     = useState(() => load(ADMIN_PROFILE));
  const [businessOwner, setBusinessOwner] = useState(() => load(OWNER_PROFILE));

  // ── Referral capture ────────────────────────────────────────────────────────
  // Persist a ?ref=CODE from the landing URL so it survives the multi-step
  // (phone/OTP) signup flow and can be attached when the account is created.
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) localStorage.setItem('nowshin_ref', ref.trim().toUpperCase());
    } catch {}
  }, []);

  // ── Auto-logout on 401 ──────────────────────────────────────────────────────
  // The API client fires 'nooshin:401' whenever a request gets a 401 response.
  // We listen here and log out whichever user type the expired token belonged to.
  useEffect(() => {
    const handle = (e) => {
      const { tokenKey } = e.detail;
      if (tokenKey === ADMIN_TOKEN_KEY) {
        setAdminUser(null);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem('nooshin_admin_profile');
      } else if (tokenKey === OWNER_TOKEN_KEY) {
        setBusinessOwner(null);
        localStorage.removeItem(OWNER_TOKEN_KEY);
        localStorage.removeItem('nooshin_owner_profile');
      } else {
        // Customer token (scoped per restaurant)
        setCustomer(null);
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(getCustomerProfileKey());
      }
    };
    window.addEventListener('nooshin:401', handle);
    return () => window.removeEventListener('nooshin:401', handle);
  }, []);

  // ── Customer ──────────────────────────────────────────────────────────────
  const saveCustomer = (profile, token) => {
    setCustomer(profile);
    localStorage.setItem(getCustomerProfileKey(), JSON.stringify(profile));
    if (token) localStorage.setItem(getCustomerTokenKey(), token);
  };

  const registerCustomer = async ({ name, email, password, phone, address }) => {
    try {
      const res = await customerApi.register({ name, email, password, phone, address });
      saveCustomer(res.data, res.token);
      return { success: true, customer: res.data };
    } catch (err) { return { success: false, error: err.message }; }
  };

  const loginCustomer = async (email, password) => {
    try {
      const res = await customerApi.login({ email, password });
      saveCustomer(res.data, res.token);
      return { success: true, customer: res.data };
    } catch (err) { return { success: false, error: err.message }; }
  };

  // Phone-number (SMS-OTP) customer auth
  const sendCustomerOtp = async (phone) => {
    try { const res = await customerApi.otpSend(phone); return { success: true, ...res }; }
    catch (err) { return { success: false, error: err.message }; }
  };
  const verifyCustomerOtp = async (phone, code, name) => {
    try {
      const res = await customerApi.otpVerify(phone, code, name);
      saveCustomer(res.data, res.token);
      return { success: true, customer: res.data, isNew: res.isNew };
    } catch (err) { return { success: false, error: err.message }; }
  };

  const logoutCustomer = () => {
    setCustomer(null);
    localStorage.removeItem(getCustomerProfileKey());
    localStorage.removeItem(getCustomerTokenKey());
  };

  // ── Admin ─────────────────────────────────────────────────────────────────
  const saveAdmin = (profile, token) => {
    setAdminUser(profile);
    localStorage.setItem(ADMIN_PROFILE, JSON.stringify(profile));
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  };

  const loginAdmin = async (restaurantSlug, identifier, password) => {
    try {
      const res = await adminApi.login({ identifier, password, restaurantSlug });
      saveAdmin(res.data, res.token);
      return { success: true };
    } catch (err) { return { success: false, error: err.message }; }
  };

  const sendAdminOtp = async (restaurantSlug, phone) => {
    try { const r = await adminApi.otpSend(restaurantSlug, phone); return { success: true, ...r }; }
    catch (err) { return { success: false, error: err.message }; }
  };
  const verifyAdminOtp = async (restaurantSlug, phone, code) => {
    try {
      const res = await adminApi.otpVerify(restaurantSlug, phone, code);
      saveAdmin(res.data, res.token);
      return { success: true };
    } catch (err) { return { success: false, error: err.message }; }
  };

  const logoutAdmin = () => {
    setAdminUser(null);
    localStorage.removeItem(ADMIN_PROFILE);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  };

  // ── Business owner ────────────────────────────────────────────────────────
  const saveOwner = (profile, token) => {
    setBusinessOwner(profile);
    localStorage.setItem(OWNER_PROFILE, JSON.stringify(profile));
    if (token) localStorage.setItem(OWNER_TOKEN_KEY, token);
  };

  const loginBusinessOwner = async (email, password) => {
    try {
      const res = await businessApi.login({ email, password });
      saveOwner(res.data, res.token);
      return { success: true };
    } catch (err) { return { success: false, error: err.message }; }
  };

  const registerBusinessOwner = async (data) => {
    try {
      let ref = '';
      try { ref = localStorage.getItem('nowshin_ref') || ''; } catch {}
      const res = await businessApi.register(ref ? { ...data, ref } : data);
      saveOwner(res.data, res.token);
      try { if (ref) localStorage.removeItem('nowshin_ref'); } catch {}
      return { success: true };
    } catch (err) { return { success: false, error: err.message }; }
  };

  // Phone-number (SMS-OTP) owner login
  const sendOwnerOtp = async (phone) => {
    try { const res = await businessApi.otpSend(phone); return { success: true, ...res }; }
    catch (err) { return { success: false, error: err.message }; }
  };
  const verifyOwnerOtp = async (phone, code, name) => {
    try {
      const res = await businessApi.otpVerify(phone, code, name);
      saveOwner(res.data, res.token);
      return { success: true, isNew: res.isNew };
    } catch (err) { return { success: false, error: err.message }; }
  };

  const logoutBusinessOwner = () => {
    setBusinessOwner(null);
    localStorage.removeItem(OWNER_PROFILE);
    localStorage.removeItem(OWNER_TOKEN_KEY);
  };

  // Merge fields into the stored owner profile (state + localStorage), e.g. after
  // saving dashboard language. No token change.
  const syncOwnerProfile = (patch) => {
    setBusinessOwner(prev => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem(OWNER_PROFILE, JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{
      customer,      registerCustomer,      loginCustomer,      logoutCustomer,
      sendCustomerOtp, verifyCustomerOtp,
      // saveCustomerProfile: update stored profile in state+localStorage (no token change)
      saveCustomerProfile: (profile) => {
        setCustomer(profile);
        localStorage.setItem(getCustomerProfileKey(), JSON.stringify(profile));
      },
      adminUser,     loginAdmin, sendAdminOtp, verifyAdminOtp,   logoutAdmin,
      businessOwner, registerBusinessOwner, loginBusinessOwner, logoutBusinessOwner,
      sendOwnerOtp, verifyOwnerOtp, syncOwnerProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
